import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight, Cloud, Compass, Ghost } from 'lucide-react'
import { useAppStore } from '@core/store'
import { nomeNaLista } from '@viewer/lib/nomeDoModelo'
import { schemaLabel } from '@viewer/lib/ifcSchema'
import { gravidadeGeral } from '@viewer/lib/georef/verificacoesGeorref'
import { useVerificacoesGeorref } from '@viewer/lib/georef/useVerificacoesGeorref'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  LEFT_SIDEBAR_MAX,
  LEFT_SIDEBAR_MIN,
} from '@viewer/store/selectionSlice'
import SidebarTabs from '@viewer/spatial-tree/SidebarTabs'
import { gravarPreferencia, lerPreferencia, limitarAltura, type PreferenciaDaLista } from './listaDosModelos'
import { resolveStoredHandle } from '@core/session/ifcHandles'
import { ifcPersistence } from '@core/session/ifcPersistence'
import {
  IDENTITY_PLACEMENT,
  placementsDiffer,
} from '@viewer/lib/geoTransform'
import type { VnSessionModel } from '@core/session/session'
import GeoPlacementEditor from './GeoPlacementEditor'
import ModelosDoWorkspace from '@core/modelos/ModelosDoWorkspace'
import SessoesPartilhadas from '@core/sessoes/SessoesPartilhadas'
import Reciclagem from '@core/modelos/Reciclagem'
import PartilhadosComigo from '@core/partilhas/PartilhadosComigo'
import EstadoPartilhaDoModelo from '@core/modelos/EstadoPartilhaDoModelo'
import {
  abrirModeloPartilhado,
  usePerguntarPartilhaAoAbrir,
  useModelosPartilhados,
} from '@core/modelos/modelosPartilhados'
import { encontrarPorSha } from '@core/modelos/modelosRegras'

function Sidebar() {
  const models = useAppStore((s) => s.models)
  const modelosEmFantasma = useAppStore((s) => s.modelosEmFantasma)
  const activeModelId = useAppStore((s) => s.activeModelId)
  const setActiveModel = useAppStore((s) => s.setActiveModel)
  const expected = useAppStore((s) => s.sessionRestoreModels)
  const width = useAppStore((s) => s.leftSidebarWidth)
  const setWidth = useAppStore((s) => s.setLeftSidebarWidth)
  const collapsed = useAppStore((s) => s.leftSidebarCollapsed)
  const setCollapsed = useAppStore((s) => s.setLeftSidebarCollapsed)
  const dragging = useRef(false)
  // Modelos partilhados no workspace: null fora de um workspace com o
  // módulo "modelos". Ao abrir um IFC novo, oferece partilhá-lo.
  const modelosPart = useModelosPartilhados()
  usePerguntarPartilhaAoAbrir(modelosPart, models)
  const [pendingStatus, setPendingStatus] = useState<Record<string, string>>({})
  // A lista dos modelos dobra-se e muda de altura (divisória por baixo),
  // para não tirar espaço à estrutura espacial.
  const [lista, setLista] = useState<PreferenciaDaLista>(() => lerPreferencia())
  const barraRef = useRef<HTMLElement | null>(null)
  const mudarLista = useCallback((p: PreferenciaDaLista) => {
    setLista(p)
    gravarPreferencia(p)
  }, [])
  const arrastoDaLista = useRef<{ y0: number; altura0: number } | null>(null)
  const aoPremirDivisoria = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    arrastoDaLista.current = { y0: e.clientY, altura0: lista.altura }
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
  }
  const aoMoverDivisoria = (e: React.PointerEvent<HTMLDivElement>) => {
    const a = arrastoDaLista.current
    if (!a) return
    const altura = limitarAltura(a.altura0 + e.clientY - a.y0, barraRef.current?.clientHeight)
    setLista((l) => ({ ...l, altura }))
  }
  const aoLargarDivisoria = () => {
    if (!arrastoDaLista.current) return
    arrastoDaLista.current = null
    setLista((l) => {
      gravarPreferencia(l)
      return l
    })
  }
  // ID of the model whose "Posição" editor is currently expanded.
  // Single-open semantics: clicking another model collapses the
  // current one. null = none open.
  const [openPlacementId, setOpenPlacementId] = useState<string | null>(null)
  // Subscribe to the geo state so the row badge ("manual" / "auto"
  // / "origem") refreshes when the user edits the placement.
  const geoBaselines = useAppStore((s) => s.geoBaselines)
  const geoOverrides = useAppStore((s) => s.geoOverrides)
  // External request to open a model's placement editor (e.g. clicking
  // the origin-marker label in the 3D view). Consume and clear.
  const placementEditorRequest = useAppStore((s) => s.placementEditorRequest)
  const clearPlacementEditorRequest = useAppStore(
    (s) => s.clearPlacementEditorRequest,
  )
  // Adotamos o pedido DURANTE o render (padrão recomendado do React para
  // "ajustar estado quando um valor externo muda", sem efeito nem
  // set-state-in-effect), com guarda por referência para consumir cada
  // pedido uma só vez. Espelha o filterPrefill do FloatingFilterPanel.
  const [lastPlacementRequest, setLastPlacementRequest] = useState<
    string | null
  >(null)
  if (placementEditorRequest && placementEditorRequest !== lastPlacementRequest) {
    setLastPlacementRequest(placementEditorRequest)
    setOpenPlacementId(placementEditorRequest)
  }
  // Abrir a barra e limpar o canal do store ficam no efeito: são setters
  // do store (efeitos sobre um sistema externo), não estado local.
  useEffect(() => {
    if (!placementEditorRequest) return
    setCollapsed(false)
    clearPlacementEditorRequest()
  }, [placementEditorRequest, clearPlacementEditorRequest, setCollapsed])

  // IFCs declared by the saved session but not yet loaded into the
  // viewer. Computed from the expected-models hint left behind by
  // Open Session minus what the store currently has.
  const pending = useMemo<VnSessionModel[]>(() => {
    if (!expected) return []
    const loaded = new Set(models.map((m) => m.sha256.toLowerCase()))
    return expected.filter((e) => !loaded.has(e.sha256.toLowerCase()))
  }, [expected, models])

  const onResizeDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragging.current = true
      ;(e.target as Element).setPointerCapture?.(e.pointerId)
    },
    [],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragging.current) return
      // Sidebar is anchored to the viewport's left edge, so visible
      // width is just the cursor X.
      setWidth(e.clientX)
    }
    const onUp = () => {
      dragging.current = false
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [setWidth])

  const onToggleVisibility = (id: string, current: boolean) => {
    viewerApi.setModelVisibility(id, !current)
  }

  const onRemove = (id: string, name: string) => {
    if (!window.confirm(`Remover o modelo "${name}"?`)) return
    void viewerApi.disposeModel(id)
  }

  // Reload an IFC from the stored FileSystemFileHandle. The handle
  // was saved at original-load time (useViewer.ts -> rememberHandle)
  // so we can re-open the same file from disk without asking the
  // user to navigate to it again, modulo a one-time permission
  // prompt the browser may show. If anything fails (handle gone,
  // permission denied, file changed on disk), we fall back to a
  // per-entry file picker so the user can re-pick manually.
  const onReopenFromHandle = async (entry: VnSessionModel) => {
    if (!viewerApi.hasIfcLoader()) {
      setPendingStatus((s) => ({
        ...s,
        [entry.sha256]: 'viewer ainda não está pronto',
      }))
      return
    }
    setPendingStatus((s) => ({ ...s, [entry.sha256]: 'a abrir…' }))

    // First try the IDB byte cache: instant, no permission prompt.
    try {
      const rec = await ifcPersistence.load(entry.sha256)
      if (rec) {
        const aberto = await viewerApi.loadIfcFile(ifcPersistence.toFile(rec), {
          replace: false,
        })
        if (aberto.ok) void ifcPersistence.touch(entry.sha256)
        setPendingStatus((s) => ({
          ...s,
          [entry.sha256]: aberto.ok ? 'reaberto da cache' : aberto.erro,
        }))
        return
      }
    } catch (err) {
      console.warn('[sidebar] cache load failed', err)
    }

    // Depois o workspace: se o ficheiro estiver partilhado, vem de lá
    // (com o SHA-256 verificado), sem pedir nada do disco.
    const partilhado = encontrarPorSha(modelosPart?.lista, entry.sha256)
    if (partilhado) {
      setPendingStatus((s) => ({ ...s, [entry.sha256]: 'a abrir do workspace…' }))
      const r = await abrirModeloPartilhado(partilhado)
      setPendingStatus((s) => ({
        ...s,
        [entry.sha256]: r === 'falhou' ? 'falhou ao abrir do workspace' : 'aberto do workspace',
      }))
      if (r !== 'falhou') return
    }

    // Then try the FileSystemFileHandle: re-reads from disk.
    const res = await resolveStoredHandle(entry.sha256, true)
    if (res.kind === 'ok') {
      try {
        const aberto = await viewerApi.loadIfcFile(res.file, { replace: false })
        setPendingStatus((s) => ({
          ...s,
          [entry.sha256]: aberto.ok ? 'reaberto do disco' : aberto.erro,
        }))
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'falhou'
        setPendingStatus((s) => ({ ...s, [entry.sha256]: msg }))
      }
      return
    }

    // Give the user actionable feedback by failure type, then offer
    // a manual file picker so they can re-supply the file on the
    // spot without leaving the panel.
    const reasonByKind: Record<string, string> = {
      permission_denied: 'permissão negada',
      gone: 'ficheiro já não existe nessa localização',
      sha_mismatch: 'ficheiro foi modificado no disco',
      missing: 'sem registo neste browser',
      unsupported: 'browser não suporta reabertura automática',
    }
    setPendingStatus((s) => ({
      ...s,
      [entry.sha256]: reasonByKind[res.kind] ?? 'erro a reabrir',
    }))
    onPickFromDisk(entry)
  }

  // Per-entry manual file picker. Used both by the "Abrir" button and
  // as a fallback when the stored handle path fails. Matches the
  // picked file by name to the expected entry; if the user picks a
  // different IFC, that's their call; the existing pipeline still
  // hashes it and adds it as a model.
  const onPickFromDisk = (entry: VnSessionModel) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.ifc'
    input.style.display = 'none'
    input.addEventListener('change', () => {
      const file = input.files?.[0]
      input.remove()
      if (!file) return
      setPendingStatus((s) => ({ ...s, [entry.sha256]: 'a carregar…' }))
      void (async () => {
        try {
          const aberto = await viewerApi.loadIfcFile(file, { replace: false })
          setPendingStatus((s) => ({
            ...s,
            [entry.sha256]: aberto.ok ? 'carregado do disco' : aberto.erro,
          }))
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'falhou ao carregar'
          setPendingStatus((s) => ({ ...s, [entry.sha256]: msg }))
        }
      })()
    })
    document.body.appendChild(input)
    input.click()
  }

  // Collapsed state: render only a thin vertical strip with an expand
  // chevron so the user always has a way to bring the sidebar back.
  if (collapsed) {
    return (
      <aside className="flex w-7 shrink-0 flex-col items-center border-r border-[var(--color-border)] bg-[var(--color-panel)]">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          title="Expandir barra lateral"
          className="mt-2 flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-panel-elevated)] hover:text-[var(--color-text)]"
          aria-label="Expandir barra lateral"
        >
          <ChevronRight size={16} />
        </button>
        <div className="mt-3 [writing-mode:vertical-rl] text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
          Estrutura · Modelos
        </div>
      </aside>
    )
  }

  return (
    <aside
      ref={barraRef}
      className="relative flex min-h-0 shrink-0 flex-col overflow-hidden border-r border-[var(--color-border)] bg-[var(--color-panel)]"
      style={{
        width: `${width}px`,
        minWidth: `${LEFT_SIDEBAR_MIN}px`,
        maxWidth: `${LEFT_SIDEBAR_MAX}px`,
      }}
    >
      <div className="flex items-center justify-between border-b border-[var(--color-border-subtle)] px-3 py-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        <button
          type="button"
          onClick={() => mudarLista({ ...lista, aberta: !lista.aberta })}
          aria-expanded={lista.aberta}
          title={lista.aberta ? 'Dobrar a lista dos modelos' : 'Abrir a lista dos modelos'}
          className="flex min-w-0 items-center gap-1 uppercase tracking-wide hover:text-[var(--color-text)]"
        >
          {lista.aberta ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
          Abertos ({models.length})
          {pending.length > 0 && (
            <span className="ml-1 normal-case text-[var(--color-warn-text)]">
              · {pending.length} por carregar
            </span>
          )}
        </button>
        <div className="flex items-center gap-1">
          {models.length > 0 && (
            <button
              type="button"
              onClick={() => viewerApi.fitToAllModels()}
              title="Centrar a câmara em todos os modelos carregados"
              className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] normal-case hover:text-[var(--color-text)]"
            >
              Centrar
            </button>
          )}
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title="Colapsar barra lateral"
            aria-label="Colapsar barra lateral"
            className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-panel-elevated)] hover:text-[var(--color-text)]"
          >
            <ChevronLeft size={14} />
          </button>
        </div>
      </div>

      <div
        className={`overflow-y-auto border-b border-[var(--color-border)] ${lista.aberta ? '' : 'hidden'}`}
        style={{ maxHeight: `${lista.altura}px` }}
      >
        {models.length === 0 && pending.length === 0 ? (
          <div className="p-3 text-sm text-[var(--color-text-dim)]">
            Nenhum modelo carregado.
            <div className="mt-2 text-xs leading-relaxed">
              Usa{' '}
              <span className="text-[var(--color-accent)]">Abrir modelo</span> no
              viewer, arrasta um ficheiro para o viewport, ou experimenta{' '}
              <span className="text-[var(--color-accent)]">Exemplo</span>.
            </div>
          </div>
        ) : (
          <ul>
            {models.map((m) => {
              const isActive = m.id === activeModelId
              const isPlacementOpen = openPlacementId === m.id
              const baseline = geoBaselines[m.id] ?? IDENTITY_PLACEMENT
              const override = geoOverrides[m.id] ?? null
              const baselineIsIdentity = !placementsDiffer(
                baseline,
                IDENTITY_PLACEMENT,
              )
              const hasManualOverride =
                override !== null && placementsDiffer(override, baseline)
              const placementStatus: 'manual' | 'auto' | 'origem' =
                hasManualOverride
                  ? 'manual'
                  : baselineIsIdentity
                    ? 'origem'
                    : 'auto'
              const placementTooltip =
                placementStatus === 'manual'
                  ? 'Posição manual: clica para editar ou reset'
                  : placementStatus === 'auto'
                    ? 'Posição via IfcMapConversion: clica para sobrescrever'
                    : 'Sem MapConversion: clica para colocar manualmente'
              return (
                <li
                  key={m.id}
                  className={`flex flex-col border-b border-[var(--color-border-subtle)] transition-colors ${
                    isActive
                      ? 'border-l-2 border-l-[var(--color-accent)] bg-[var(--color-panel-elevated)]'
                      : 'hover:bg-[var(--color-panel-elevated)]'
                  }`}
                >
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => onToggleVisibility(m.id, m.visible)}
                    title={m.visible ? 'Ocultar' : 'Mostrar'}
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border border-transparent ${
                      m.visible
                        ? 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                        : 'text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)]'
                    }`}
                    aria-label={m.visible ? 'Ocultar' : 'Mostrar'}
                  >
                    <svg
                      viewBox="0 0 14 14"
                      width="14"
                      height="14"
                      aria-hidden
                    >
                      {m.visible ? (
                        <>
                          <path
                            d="M1 7 C2.5 4 4.5 3 7 3 C9.5 3 11.5 4 13 7 C11.5 10 9.5 11 7 11 C4.5 11 2.5 10 1 7 Z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={1.2}
                          />
                          <circle cx="7" cy="7" r="1.8" fill="currentColor" />
                        </>
                      ) : (
                        <>
                          <path
                            d="M1 7 C2.5 4 4.5 3 7 3 C9.5 3 11.5 4 13 7 C11.5 10 9.5 11 7 11 C4.5 11 2.5 10 1 7 Z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={1.2}
                            opacity={0.5}
                          />
                          <line
                            x1="2"
                            y1="2"
                            x2="12"
                            y2="12"
                            stroke="currentColor"
                            strokeWidth={1.4}
                            strokeLinecap="round"
                          />
                        </>
                      )}
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => void viewerApi.setModeloEmFantasma(m.id, !modelosEmFantasma.includes(m.id))}
                    aria-pressed={modelosEmFantasma.includes(m.id)}
                    data-campo="modelo-em-fantasma"
                    title={
                      modelosEmFantasma.includes(m.id)
                        ? 'Em fantasma: só as arestas, para ver o que está dentro. Clique para voltar ao normal.'
                        : 'Pôr este modelo em fantasma (só as arestas, esbatidas), sem precisar de seleccionar nada. Útil para ver as instalações dentro da arquitectura e da estrutura.'
                    }
                    aria-label={modelosEmFantasma.includes(m.id) ? 'Tirar do fantasma' : 'Pôr em fantasma'}
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      modelosEmFantasma.includes(m.id)
                        ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                        : 'border-transparent text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)]'
                    }`}
                  >
                    <Ghost size={12} aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveModel(m.id)}
                    className="min-w-0 flex-1 text-left"
                    title={`#${m.id} · ${schemaLabel(m.schemaVersion)}`}
                  >
                    <div
                      className={`truncate text-sm font-medium ${
                        m.visible
                          ? isActive
                            ? 'text-[var(--color-text)]'
                            : 'text-[var(--color-text-muted)]'
                          : 'text-[var(--color-text-dim)] line-through decoration-[var(--color-text-dim)]/50'
                      }`}
                    >
                      {/* Dois modelos do mesmo ficheiro distinguem-se pelo
                          id ("Nome (2).ifc"); sozinho, mostra o nome. */}
                      {/* A mesma regra que o nome curto do modelo usa
                          (viewer/lib/nomeDoModelo.ts). */}
                      {nomeNaLista(m, models)}
                    </div>
                    <div className="mt-0.5 text-xs text-[var(--color-text-dim)]">
                      {schemaLabel(m.schemaVersion)}
                      <SeloGeorref modelId={m.id} />
                      {m.rawIfcAvailable === false && (
                        // Um Fragments aberto directamente nao traz o
                        // IFC. Quem o abriu tem de o ver aqui, antes de
                        // perguntar ao assistente ou de correr uma
                        // validacao e receber zero por resposta.
                        <span
                          className="ml-1.5 rounded bg-[var(--color-accent-bg)] px-1 py-0.5 text-[10px] text-[var(--color-warn-text)]"
                          title="Aberto em Fragments: nao traz o IFC. O IDS, o BoQ, a Tabela, o IFCEdit, a escrita de georreferenciacao e o assistente precisam do IFC e nao vao poder responder sobre este modelo."
                        >
                          sem IFC
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setOpenPlacementId(isPlacementOpen ? null : m.id)
                    }
                    title={placementTooltip}
                    aria-label="Editar posição"
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border border-transparent ${
                      isPlacementOpen
                        ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                        : placementStatus === 'manual'
                          ? 'text-[var(--color-warn-text)] hover:text-[var(--color-warn-text)]'
                          : placementStatus === 'auto'
                            ? 'text-[var(--color-ok-text)]/80 hover:text-[var(--color-ok-text)]'
                            : 'text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)]'
                    }`}
                  >
                    <Compass size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemove(m.id, m.fileName)}
                    title="Remover modelo"
                    aria-label="Remover"
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-transparent text-[var(--color-text-dim)] hover:border-red-700/50 hover:bg-[var(--color-error-bg)] hover:text-[var(--color-error-text)]"
                  >
                    <svg viewBox="0 0 14 14" width="12" height="12" aria-hidden>
                      <path
                        d="M4 4 L10 10 M10 4 L4 10"
                        stroke="currentColor"
                        strokeWidth={1.4}
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
                {modelosPart?.estado?.ativo && modelosPart.lista && (
                  <div className="px-8 pb-1">
                    <EstadoPartilhaDoModelo model={m} estado={modelosPart} />
                  </div>
                )}
                {isPlacementOpen && (
                  <GeoPlacementEditor
                    modelId={m.id}
                    onClose={() => setOpenPlacementId(null)}
                  />
                )}
                </li>
              )
            })}
            {pending.map((p) => {
              const status = pendingStatus[p.sha256]
              return (
                <li
                  key={`pending:${p.sha256}`}
                  className="flex items-center gap-1 border-b border-[var(--color-border-subtle)] bg-[var(--color-accent-bg)] px-2 py-1.5"
                  title={
                    'IFC declarado pela sessão guardada mas ainda não carregado. '
                    + 'Os browsers não expõem o caminho absoluto ao programa, mas o '
                    + 'viewer guardou um handle deste ficheiro; clica em "Reabrir" para '
                    + 'o trazer de volta do disco (talvez precise de re-autorizar a '
                    + 'leitura à primeira vez).'
                  }
                >
                  <span
                    className="flex h-5 w-5 shrink-0 items-center justify-center text-[var(--color-warn-text)]"
                    aria-hidden
                  >
                    <svg viewBox="0 0 14 14" width="12" height="12">
                      <circle
                        cx="7"
                        cy="7"
                        r="5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.4}
                        strokeDasharray="2 2"
                      />
                    </svg>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-[var(--color-warn-text)]">
                      {p.filename}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-[var(--color-warn-text)]">
                      {status ?? 'por carregar'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void onReopenFromHandle(p)}
                    title="Reabrir o ficheiro a partir do disco usando o handle guardado neste browser. Se o browser pedir permissão, autoriza."
                    className="rounded border border-[var(--color-warn-border)] px-1.5 py-0 text-[10px] text-[var(--color-warn-text)] hover:bg-[var(--color-accent-bg)]"
                  >
                    Reabrir
                  </button>
                  <button
                    type="button"
                    onClick={() => onPickFromDisk(p)}
                    title="Escolher manualmente o ficheiro IFC do disco."
                    className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  >
                    Abrir…
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {lista.aberta && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Altura da lista dos modelos"
          title="Arrastar para mudar a altura da lista dos modelos (duplo clique repõe)"
          onPointerDown={aoPremirDivisoria}
          onPointerMove={aoMoverDivisoria}
          onPointerUp={aoLargarDivisoria}
          onPointerCancel={aoLargarDivisoria}
          onDoubleClick={() => mudarLista({ ...lista, altura: 240 })}
          className="h-1.5 shrink-0 cursor-row-resize bg-transparent hover:bg-[var(--color-accent)]/40"
        />
      )}

      {modelosPart && (
        // Encolhe e rola quando falta altura (BCF acoplado em baixo): antes
        // transbordava por cima do painel do módulo (Rui, 16/10/2026).
        <div className="flex min-h-0 shrink flex-col overflow-y-auto bg-[var(--color-surface)]" data-campo="no-servidor">
          <div
            className="flex items-center gap-1.5 border-y border-[var(--color-border)] px-3 py-1 text-[10px] uppercase tracking-wide text-[var(--color-text-dim)]"
            title="Guardado na plataforma. Cada pessoa só vê o que lhe foi partilhado; abrir traz para cima, para a lista dos abertos."
          >
            <Cloud size={11} aria-hidden />
            No servidor
            <span className="normal-case tracking-normal">· só o que pode ver</span>
          </div>
          <ModelosDoWorkspace estado={modelosPart} />
          <SessoesPartilhadas />
          <Reciclagem estado={modelosPart} />
          <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-1.5 empty:hidden">
            <PartilhadosComigo compacto />
          </div>
        </div>
      )}
      {!modelosPart && (
        <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-1.5 empty:hidden">
          <PartilhadosComigo compacto />
        </div>
      )}

      <div className="min-h-0 flex-1">
        <SidebarTabs />
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        onPointerDown={onResizeDown}
        title="Arrastar para redimensionar"
        className="absolute -right-[5px] top-0 z-30 h-full w-[10px] cursor-col-resize hover:bg-[var(--color-accent)]/40"
      />
    </aside>
  )
}

export default Sidebar

/** O nível LoGeoRef do modelo, com a cor da pior verificação, para se
 *  ver na lista sem abrir o Info do modelo (onde está a lista toda). */
function SeloGeorref({ modelId }: { modelId: string }) {
  const r = useVerificacoesGeorref(modelId)
  if (!r) return null
  const g = gravidadeGeral(r)
  const erros = r.verificacoes.filter((v) => v.gravidade === 'erro').length
  const avisos = r.verificacoes.filter((v) => v.gravidade === 'aviso').length
  const cor =
    g === 'erro'
      ? 'border-[var(--color-error-border)] bg-[var(--color-error-bg)] text-[var(--color-error-text)]'
      : g === 'aviso'
        ? 'border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] text-[var(--color-warn-text)]'
        : 'border-[var(--color-ok-border)] bg-[var(--color-ok-bg)] text-[var(--color-ok-text)]'
  const resumo = [erros > 0 ? `${erros} erro${erros === 1 ? '' : 's'}` : '', avisos > 0 ? `${avisos} aviso${avisos === 1 ? '' : 's'}` : '']
    .filter(Boolean)
    .join(', ')
  return (
    <span
      className={`ml-1.5 rounded border px-1 py-0.5 font-mono text-[10px] ${cor}`}
      title={`Georreferência: LoGeoRef ${r.nivel}${resumo ? `, ${resumo}` : ', sem erros nem avisos'}. A lista das verificações está no Info do modelo.`}
      data-campo="selo-georref"
      data-gravidade={g}
    >
      LoGeoRef {r.nivel}
    </span>
  )
}
